import React, { useState, useEffect } from 'react';
import { useAdminStore } from '../../../lib/store-admin';
import { Module, ModuleItem, QuizItem, MiniSimItem, MainSimItem } from '../../../lib/supabase';
import './module-editor.scss';
import avatarsData from '../../../data/avatars.json';

interface ModuleEditorProps {
    institutionId: string;
    module?: Module;
    onClose: () => void;
}

const VOICE_OPTIONS = [
    { value: "Puck", label: "Puck" },
    { value: "Charon", label: "Charon" },
    { value: "Kore", label: "Kore" },
    { value: "Fenrir", label: "Fenrir" },
    { value: "Aoede", label: "Aoede" },
    { value: "Atari02", label: "Atari02" }
];

const DEFAULT_AVATARS = [
    { value: "/avatars/julia.glb", label: "Julia (Standard)" },
    { value: "/avatars/david.glb", label: "David (Sales)" }
];

const AVATAR_FILES = avatarsData.length > 0 ? avatarsData : DEFAULT_AVATARS;

// ===== QUIZ FORM =====
const QuizForm: React.FC<{ item: QuizItem; onChange: (item: QuizItem) => void; onRemove: () => void }> = ({ item, onChange, onRemove }) => (
    <div className="module-item-card quiz-card">
        <div className="item-card-header">
            <span className="item-type-badge quiz">Soru</span>
            <button className="btn-remove-item" onClick={onRemove}>
                <span className="material-symbols-outlined">close</span>
            </button>
        </div>
        <div className="form-group">
            <label>Soru Metni</label>
            <textarea
                value={item.question}
                onChange={(e) => onChange({ ...item, question: e.target.value })}
                placeholder="Soruyu yazın..."
                rows={2}
            />
        </div>
        <div className="form-group">
            <label>Şıklar</label>
            {item.options.map((opt, idx) => (
                <div key={idx} className="option-row">
                    <button
                        type="button"
                        className={`correct-toggle ${item.correctIndex === idx ? 'active' : ''}`}
                        onClick={() => onChange({ ...item, correctIndex: idx })}
                        title="Doğru cevap olarak işaretle"
                    >
                        <span className="material-symbols-outlined">
                            {item.correctIndex === idx ? 'check_circle' : 'radio_button_unchecked'}
                        </span>
                    </button>
                    <input
                        type="text"
                        value={opt}
                        onChange={(e) => {
                            const newOptions = [...item.options];
                            newOptions[idx] = e.target.value;
                            onChange({ ...item, options: newOptions });
                        }}
                        placeholder={`Şık ${idx + 1}`}
                    />
                </div>
            ))}
        </div>
    </div>
);

// ===== MINI SIM FORM =====
const MiniSimForm: React.FC<{ item: MiniSimItem; onChange: (item: MiniSimItem) => void; onRemove: () => void }> = ({ item, onChange, onRemove }) => (
    <div className="module-item-card mini-sim-card">
        <div className="item-card-header">
            <span className="item-type-badge mini-sim">Mini Simülasyon</span>
            <button className="btn-remove-item" onClick={onRemove}>
                <span className="material-symbols-outlined">close</span>
            </button>
        </div>
        <div className="form-row-2col">
            <div className="form-group">
                <label>3D Model</label>
                <select value={item.avatarUrl} onChange={(e) => onChange({ ...item, avatarUrl: e.target.value })}>
                    {AVATAR_FILES.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>
            <div className="form-group">
                <label>Ses</label>
                <select value={item.voice} onChange={(e) => onChange({ ...item, voice: e.target.value })}>
                    {VOICE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>
        </div>
        <div className="form-group">
            <label>AI'ın Soracağı Soru</label>
            <textarea
                value={item.aiQuestion}
                onChange={(e) => onChange({ ...item, aiQuestion: e.target.value })}
                placeholder="AI'ın kullanıcıya soracağı soru..."
                rows={2}
            />
        </div>
        <div className="form-group">
            <label>Beklenen Cevap</label>
            <textarea
                value={item.expectedAnswer}
                onChange={(e) => onChange({ ...item, expectedAnswer: e.target.value })}
                placeholder="Kullanıcının vermesi gereken cevap..."
                rows={2}
            />
        </div>
    </div>
);

// ===== MAIN SIM FORM =====
const MainSimForm: React.FC<{ item: MainSimItem; onChange: (item: MainSimItem) => void; onRemove: () => void }> = ({ item, onChange, onRemove }) => {
    const [newMission, setNewMission] = useState('');

    return (
        <div className="module-item-card main-sim-card">
            <div className="item-card-header">
                <span className="item-type-badge main-sim">Ana Simülasyon</span>
                <button className="btn-remove-item" onClick={onRemove}>
                    <span className="material-symbols-outlined">close</span>
                </button>
            </div>
            <div className="form-row-2col">
                <div className="form-group">
                    <label>3D Model</label>
                    <select value={item.avatarUrl} onChange={(e) => onChange({ ...item, avatarUrl: e.target.value })}>
                        {AVATAR_FILES.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label>Ses</label>
                    <select value={item.voice} onChange={(e) => onChange({ ...item, voice: e.target.value })}>
                        {VOICE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="form-group">
                <label>Görevler</label>
                <div className="mission-input-row">
                    <input
                        type="text"
                        value={newMission}
                        onChange={(e) => setNewMission(e.target.value)}
                        placeholder="Görev ekle..."
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && newMission.trim()) {
                                e.preventDefault();
                                onChange({ ...item, missions: [...item.missions, newMission.trim()] });
                                setNewMission('');
                            }
                        }}
                    />
                    <button type="button" className="btn-add-mission" onClick={() => {
                        if (newMission.trim()) {
                            onChange({ ...item, missions: [...item.missions, newMission.trim()] });
                            setNewMission('');
                        }
                    }}>
                        <span className="material-symbols-outlined">add</span>
                    </button>
                </div>
                <div className="missions-list">
                    {item.missions.map((m, idx) => (
                        <div key={idx} className="mission-chip">
                            <span>{m}</span>
                            <button onClick={() => onChange({ ...item, missions: item.missions.filter((_, i) => i !== idx) })}>
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                            </button>
                        </div>
                    ))}
                </div>
            </div>
            <div className="form-group">
                <label>Konu / Persona Talimatları</label>
                <textarea
                    value={item.systemInstruction}
                    onChange={(e) => onChange({ ...item, systemInstruction: e.target.value })}
                    placeholder="AI'ın davranışını ve simülasyon konusunu belirleyin..."
                    rows={6}
                />
            </div>
            <div className="form-group">
                <label>Kullanıcı Yönergesi</label>
                <textarea
                    value={item.userInstruction}
                    onChange={(e) => onChange({ ...item, userInstruction: e.target.value })}
                    placeholder="Kullanıcıya gösterilecek talimatlar..."
                    rows={4}
                />
            </div>
        </div>
    );
};


// ===== MODULE EDITOR =====
export const ModuleEditor: React.FC<ModuleEditorProps> = ({ institutionId, module, onClose }) => {
    const { addModule, updateModule, isLoading } = useAdminStore();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [items, setItems] = useState<ModuleItem[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [showAddMenu, setShowAddMenu] = useState(false);

    useEffect(() => {
        if (module) {
            setTitle(module.title);
            setDescription(module.description || '');
            setItems(module.items || []);
        } else {
            setTitle('');
            setDescription('');
            setItems([]);
        }
    }, [module]);

    const addItem = (type: 'quiz' | 'mini_sim' | 'main_sim') => {
        let newItem: ModuleItem;
        switch (type) {
            case 'quiz':
                newItem = { type: 'quiz', question: '', options: ['', '', '', ''], correctIndex: 0 };
                break;
            case 'mini_sim':
                newItem = { type: 'mini_sim', avatarUrl: '/avatars/julia.glb', voice: 'Puck', aiQuestion: '', expectedAnswer: '' };
                break;
            case 'main_sim':
                newItem = { type: 'main_sim', avatarUrl: '/avatars/julia.glb', voice: 'Puck', missions: [], systemInstruction: '', userInstruction: '' };
                break;
        }
        setItems([...items, newItem]);
        setShowAddMenu(false);
    };

    const updateItem = (index: number, updatedItem: ModuleItem) => {
        const newItems = [...items];
        newItems[index] = updatedItem;
        setItems(newItems);
    };

    const removeItem = (index: number) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        setError(null);
        if (!title.trim()) {
            setError('Modül başlığı zorunludur');
            return;
        }
        if (items.length === 0) {
            setError('En az bir adım eklemelisiniz');
            return;
        }

        try {
            let result;
            if (module) {
                result = await updateModule(module.id, { title, description, items } as any);
            } else {
                result = await addModule(institutionId, title, description, items);
            }

            if (result.success) {
                onClose();
            } else {
                setError(result.error || 'Kaydetme başarısız');
            }
        } catch (err: any) {
            setError(err.message);
        }
    };

    return (
        <div className="module-editor">
            <div className="editor-header">
                <button className="btn-back" onClick={onClose}>
                    <span className="material-symbols-outlined">arrow_back</span>
                </button>
                <h2>{module ? 'Modül Düzenle' : 'Yeni Modül'}</h2>
                <div className="actions">
                    <button className="btn-cancel" onClick={onClose}>İptal</button>
                    <button className="btn-save" onClick={handleSubmit} disabled={isLoading}>
                        {isLoading ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                </div>
            </div>

            {error && <div className="error-banner">{error}</div>}

            <div className="editor-body">
                <div className="module-info-section">
                    <div className="form-group">
                        <label>Modül Başlığı</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Örn: Turkcell İnternet Paketleri Eğitimi"
                        />
                    </div>
                    <div className="form-group">
                        <label>Açıklama</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Modülün kısa açıklaması..."
                            rows={2}
                        />
                    </div>
                </div>

                <div className="items-section">
                    <div className="items-header">
                        <h3>
                            <span className="material-symbols-outlined">list</span>
                            Modül Adımları ({items.length})
                        </h3>
                        <div className="add-menu-container">
                            <button className="btn-add-item" onClick={() => setShowAddMenu(!showAddMenu)}>
                                <span className="material-symbols-outlined">add</span>
                                Adım Ekle
                            </button>
                            {showAddMenu && (
                                <div className="add-menu-dropdown">
                                    <button onClick={() => addItem('quiz')}>
                                        <span className="material-symbols-outlined">quiz</span>
                                        Soru
                                    </button>
                                    <button onClick={() => addItem('mini_sim')}>
                                        <span className="material-symbols-outlined">mic</span>
                                        Mini Simülasyon
                                    </button>
                                    <button onClick={() => addItem('main_sim')}>
                                        <span className="material-symbols-outlined">smart_toy</span>
                                        Ana Simülasyon
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="items-list">
                        {items.length === 0 && (
                            <div className="empty-items">
                                <span className="material-symbols-outlined">playlist_add</span>
                                <p>Henüz adım eklenmemiş. "Adım Ekle" butonuna tıklayarak soru, mini simülasyon veya ana simülasyon ekleyin.</p>
                            </div>
                        )}
                        {items.map((item, index) => (
                            <div key={index} className="item-wrapper">
                                <div className="step-number">{index + 1}</div>
                                {item.type === 'quiz' ? (
                                    <QuizForm item={item} onChange={(updated) => updateItem(index, updated)} onRemove={() => removeItem(index)} />
                                ) : item.type === 'mini_sim' ? (
                                    <MiniSimForm item={item as MiniSimItem} onChange={(updated) => updateItem(index, updated)} onRemove={() => removeItem(index)} />
                                ) : (
                                    <MainSimForm item={item as MainSimItem} onChange={(updated) => updateItem(index, updated)} onRemove={() => removeItem(index)} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
